import React, { Dispatch, SetStateAction, useEffect, useMemo } from "react";
import { useGetMyOrderByIdQuery } from "../../../../../../../../../API/authApi";
import { formatDate, formatTime } from "../../../../../../../../../Utilits/utilits";
import { useNoAuthHooks } from "../../../../../../../../../Hooks/hooks";
import useMyOrderCards from "../Cards/myOrderCard-hook";
import { IBusinessProfile, IOrderResponse } from "../../../../../../../../../Interfaces/responseTypes";
import {
  ICartItem,
  IDeliveryOrderData,
  IOrderParams,
  IPreOrderData,
  IReserveOrderData,
  OrderTypes,
} from "../../../../../../../../../Interfaces/componentTypes";
import useLanguagesHooks from "../../../../../../../../../Components/Languages/setLanguage-hooks";
import { MyOrderStatus } from "../MyOrderStatus/myOrderStatus";
import { VisitorPageContents } from "../../../../../../visitorPage";
import { ArrowLeftIcon, PhoneIcon } from "../../../../../../../../../Components/IconComponents";
import styles from "./orderItem.module.scss";

interface IProps {
  order: IOrderResponse;
  onBack: () => void;
  business_profile?: IBusinessProfile,
  setContentType: Dispatch<SetStateAction<VisitorPageContents>>,
}

const OrderItem: React.FC<IProps> = ({ order, business_profile, onBack, setContentType }) => {
  const { t, activeLanguage } = useLanguagesHooks();
  const { baseURL, navigate } = useNoAuthHooks();
  const { getOrderCardContent, truncateCompanyName } = useMyOrderCards();

  const { data: freshOrder } = useGetMyOrderByIdQuery(
    {
      id: String(order.id),
      phone: localStorage.getItem("phone")?.replace(/^\+/, "") || "",
      user_uuid: localStorage.getItem("user_id") || "",
    },
    { pollingInterval: 30_000 }
  );

  const currentOrder = freshOrder ?? order;

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const orderParams: IOrderParams = JSON.parse(currentOrder.order_parameters);
  const orderData: IDeliveryOrderData | IPreOrderData | IReserveOrderData = JSON.parse(currentOrder.data);

  const { typeLabel } = getOrderCardContent(
    currentOrder.order_type,
    orderParams,
    currentOrder.data
  );

  const groupedCart = useMemo(() => {
    const grouped: Record<string, ICartItem[]> = {};
    orderData.cart.forEach((item) => {
      const categoryName =
        (item.category[
          `name_${activeLanguage}` as keyof typeof item.category
        ] as string) || item.category.name_en;

      if (!grouped[categoryName]) {
        grouped[categoryName] = [];
      }
      grouped[categoryName].push(item);
    });
    return grouped;
  }, [orderData.cart, activeLanguage]);
  const getLocalizedTitle = (item: ICartItem): string =>
    (item[`name_${activeLanguage}` as keyof ICartItem] as string) ||
    item.name_en;

  const onNavigate = async () => {
    navigate(
      `/${orderParams.company_name}/menu/${orderParams.company_id}/?tab=${business_profile ? VisitorPageContents.PROFILE : VisitorPageContents.MENU
      }`
    );
    setContentType(business_profile ? VisitorPageContents.PROFILE : VisitorPageContents.MENU)
  };


  return (
    <div className={styles.orderItem}>
      <div className={styles.header}>
        <button onClick={onBack} className={styles.backBtn}>
          <ArrowLeftIcon color='var(--primary-hover)' />
        </button>
        <div className={styles.headerTitleBox}>
          <p className={styles.orderType}>
            {typeLabel} # {order.id}
          </p>
          <p className={styles.orderDate}>
            {formatDate(order.created_at)}
          </p>
        </div>
        <div />
      </div>

      <div className={styles.info}>
        <p >
          <strong>{t("Visitor.My_Order_Card.Company")}</strong>
          <span className={styles.companyTitle} onClick={onNavigate}>{orderParams.company_name}</span>
        </p>
        <p>
          <strong>{t("Visitor.My_Order_Card.Status")}</strong>  <MyOrderStatus status={order.status} />
        </p>
        <p>
          <strong>{t("Visitor.My_Order_Card.Date")}</strong>
          {formatDate(orderParams.date!, false)} {formatTime(orderParams.time!)}
        </p>
        {orderParams.address && (
          <p>
            <strong>{t("Visitor.My_Order_Card.Address")}</strong> {orderParams.address}
          </p>
        )}
        {
          orderParams.persons_count
          &&
          <p>
            <strong>{t('Visitor.Reserve.Persons')}:</strong> {orderParams.persons_count}
          </p>
        }
        {orderParams.phone_number && (
          <p>
            <strong>{t("Visitor.My_Order_Card.Phone")}</strong> {orderParams.phone_number}
          </p>
        )}
        {currentOrder.order_type === OrderTypes.DELIVERY && (orderParams.cash || orderParams.card) && (
          <p>
            <strong>{t("Visitor.My_Order_Card.Payment")}</strong>{" "}
            {orderParams.card ? t("Visitor.My_Order_Card.Card") : t("Visitor.My_Order_Card.Cash")}
          </p>
        )}
        {orderParams.notes && (
          <p>
            <strong>{t("Visitor.My_Order_Card.Notes")}</strong> {orderParams.notes}
          </p>
        )}
      </div>

      {/* Cart */}
      {
        orderData.cart.length > 0
        &&
        <div className={styles.cart}>
          {Object.entries(groupedCart).map(([categoryName, items]) => (
            <div key={categoryName} className={styles.categoryBlock}>
              <h4 className={styles.categoryHeader}>{categoryName}</h4>
              {items.map((item) => (
                <div className={styles.cartBox}>
                  <div key={item.id} className={styles.cartItem}>
                    <div className={styles.itemTitleBox}>
                      <img src={`${baseURL}${item.image}`} alt={`${item.image}.image`} />
                      <p>
                        {getLocalizedTitle(item)}
                      </p>
                    </div>
                    {item.option.length > 0 && (
                      <ul>
                        {item.option.map((opt, i) => (
                          <li key={i} className={styles.optionCount}>
                            {opt.count} × {opt.price} ֏
                          </li>
                        ))}
                      </ul>
                    )}

                  </div>
                  {item.note && (
                    <div className={styles.noteBox}>
                      <p>
                        <strong>{t("Visitor.My_Order_Card.Notes")} </strong> {item.note}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ))}
        </div>
      }

      {/* Total */}
     {
          orderData.cart.length > 0
          &&
          <div className={styles.total}>
            {"delivery_cost" in orderData && (
              <p>
                <strong>{t("Visitor.My_Order_Card.Delivery_Fee")} </strong> {orderData.delivery_cost} ֏
              </p>
            )}
            {"packageFee" in orderData && (
              <p>
                <strong>{t("Visitor.Delivery.Package_Cost")}: </strong> {orderData.packageFee} ֏
              </p>
            )}
            {
              "service_fee" in orderData && (
                <p>
                  <strong>{t('Visitor.Service_Fee')}: </strong> {(orderData as IReserveOrderData).service_fee} %
                </p>
              )
            }
            <p className={styles.totalPrice}>
              <strong >{t("Visitor.Delivery.Total")}: </strong> {orderData.total_cost} ֏
            </p>
          </div>
        }

      {/* Contact Business */}
      {orderParams.company_contacts && orderParams.company_contacts.length > 0 && (
        <div className={styles.contactBox}>
          <div className={styles.contacts}>
            <p>{t("Visitor.My_Order_Card.Contact")}</p>
            <div className={styles.phoneNumbers}>
              <PhoneIcon />
              <div>
                {orderParams.company_contacts.map((phone, idx) => (
                  <div key={idx} className={styles.phone}>
                    <a href={`tel:${phone}`} className={styles.phoneLink}>
                      {phone}
                    </a>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className={styles.logoBox} onClick={onNavigate}>
            <img src={`${baseURL}${orderParams.company_logo}`} alt='logo.png' className={styles.logo} />
            <p className={styles.companyName}> {truncateCompanyName(orderParams.company_name)}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderItem;
